'use client';

import {
  Drawer,
  Box,
  Avatar,
  Typography,
  SystemIcon,
  IconButton,
  Button,
  NewBadge,
  Divider,
} from '@wishket/design-system';
import { useRouter } from 'next/navigation';

import { Link } from '@/shared/Components';
import { Profile } from '@/shared/types';
import { PATH } from '@/shared/constants';
import { useDeviceCheck } from '@/shared/hooks';

interface MobileDrawerProps {
  profile: Profile | null;
  nextPath: string;
  authNextParams: string;
  isOpen: boolean;
  onClose: () => void;
  openProfileModal: () => void;
  openProfileImageChangeModal: () => void;
}
const UserMenuDrawer = ({
  isOpen,
  onClose,
  profile,
  nextPath,
  authNextParams,
  openProfileImageChangeModal,
  openProfileModal,
}: MobileDrawerProps) => {
  const router = useRouter();
  const { isDesktop } = useDeviceCheck();

  const mouseOverLink = (path: string) => {
    router.prefetch(path);
  };

  const clickLink = (path: string) => {
    onClose();
    router.push(path);
  };

  const clickProfileEdit = () => {
    onClose();
    openProfileModal();
  };

  const clickProfileImageChange = () => {
    onClose();
    openProfileImageChangeModal();
  };

  if (isDesktop) return null;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} className="w-4/5 bg-w-gray-10">
      <Box
        className="flex h-full w-full flex-col justify-between"
        data-testid="user-menu-drawer"
      >
        <Box className="flex items-center justify-between gap-2 px-5 py-3.5">
          <Box className="flex w-full items-center justify-start gap-4">
            <Avatar
              size="md"
              isEditable={!!profile}
              imgUrl={profile?.imgSrc}
              onClick={!profile ? undefined : clickProfileImageChange}
            />
            {profile ? (
              <Typography
                variant="body2"
                className="font-medium text-w-gray-900"
              >
                {profile.name}
              </Typography>
            ) : (
              <Link href={`${PATH.AUTH_LOGIN}?next=${authNextParams}`}>
                <Box className="flex cursor-pointer items-center justify-start gap-1">
                  <Typography variant="body2" className="text-w-gray-900">
                    로그인
                  </Typography>
                  <SystemIcon name="small_arrow_right" />
                </Box>
              </Link>
            )}
          </Box>
          <IconButton
            data-testid="mobile-drawer-close-button"
            onClick={onClose}
          >
            <SystemIcon name="large_delete" />
          </IconButton>
        </Box>
        <Box
          data-testid="mobile-drawer-sign-container"
          className="flex flex-col gap-2 px-5 pb-6 pt-2"
        >
          {profile ? (
            <>
              <Button
                size="sm"
                type="button"
                className="w-full text-w-gray-600"
                onClick={clickProfileEdit}
              >
                프로필 수정
              </Button>
              {profile.isAuthor && (
                <Box
                  onMouseOver={() =>
                    mouseOverLink(`${PATH.HOME}/@${profile.authorIdentifier}`)
                  }
                  onClick={() =>
                    clickLink(`${PATH.HOME}/@${profile.authorIdentifier}`)
                  }
                >
                  <Button
                    size="sm"
                    type="button"
                    className="w-full border-primary bg-y-primary-10 text-y-primary"
                  >
                    작가 프로필
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Link href={`${PATH.AUTH_SIGNUP}?next=${authNextParams}`}>
              <Button
                variant="solid"
                type="button"
                size="sm"
                className="w-full hover:bg-primary"
              >
                회원가입
              </Button>
            </Link>
          )}
        </Box>
        <Box className="flex flex-1 flex-col gap-3 overflow-y-auto">
          {profile && (
            <Box className="bg-w-white py-2">
              <Box
                onMouseOver={() => mouseOverLink(PATH.HOME)}
                onClick={() => clickLink(PATH.HOME)}
                data-testid="mobile-drawer-home-button"
              >
                <Box className="px-5 py-3">
                  <Typography
                    variant="body1"
                    className="font-normal text-w-gray-900"
                  >
                    홈
                  </Typography>
                </Box>
              </Box>
              <Box
                onMouseOver={() => mouseOverLink(PATH.MY_COLLECTION)}
                onClick={() => clickLink(PATH.MY_COLLECTION)}
              >
                <Box className="px-5 py-3">
                  <Typography
                    variant="body1"
                    className="relative font-normal text-w-gray-900"
                  >
                    내 컬렉션
                    {profile.hasNewCollection && (
                      <NewBadge className="absolute -right-2 top-0" />
                    )}
                  </Typography>
                </Box>
              </Box>
              <Box
                onMouseOver={() => mouseOverLink(PATH.NOTIFICATION_SETTINGS)}
                onClick={() => clickLink(PATH.NOTIFICATION_SETTINGS)}
              >
                <Box className=" px-5 py-3">
                  <Typography
                    variant="body1"
                    className="font-normal text-w-gray-900"
                  >
                    알림 설정
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
          <Box className="bg-w-white py-2" data-testid="drawer-list-container">
            <Link href={PATH.YOZM_INTRODUCE} target="_blank">
              <Box className="cursor-pointer px-5 py-3">
                <Typography
                  variant="body2"
                  className="font-normal text-w-gray-600"
                >
                  요즘IT 소개
                </Typography>
              </Box>
            </Link>
            <Link href={PATH.YOZM_CONTENTS_PROPOSAL} target="_blank">
              <Box className="cursor-pointer px-5 py-3">
                <Typography
                  variant="body2"
                  className="font-normal text-w-gray-600"
                >
                  콘텐츠 제안하기
                </Typography>
              </Box>
            </Link>
            <Link href={PATH.YOZM_ADVERTISEMENT} target="_blank">
              <Box className="cursor-pointer px-5 py-3">
                <Typography
                  variant="body2"
                  className="font-normal text-w-gray-600"
                >
                  광고 상품 보기
                </Typography>
              </Box>
            </Link>
          </Box>
        </Box>
        {profile && (
          <Box className="flex items-center gap-4 border-t border-w-gray-100 bg-w-white p-5">
            <Link target="_blank" href={PATH.AUTH_SETTINGS}>
              <Typography variant="caption1" className="text-w-gray-400">
                계정 설정
              </Typography>
            </Link>
            <Divider isVertical className="h-3" />
            <Link
              href={`${PATH.AUTH_LOGOUT}?next=${nextPath}`}
              prefetch={false}
            >
              <Typography variant="caption1" className="text-w-gray-400">
                로그아웃
              </Typography>
            </Link>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default UserMenuDrawer;
